import PropTypes from 'prop-types';
import { useState, useCallback } from 'react';

function JsonNode({ keyName, value, depth, defaultExpanded }) {
	const [expanded, setExpanded] = useState(defaultExpanded);
	const indent = depth * 20;

	const isObject = value !== null && typeof value === 'object' && !Array.isArray(value);
	const isArray = Array.isArray(value);
	const isCollapsible = isObject || isArray;

	const lineStyle = { paddingLeft: indent };

	if (!isCollapsible) {
		const rendered = renderValue(value);
		return (
			<div className="dsv-jv__line" style={lineStyle}>
				<span className="dsv-jv__toggle" style={{ visibility: 'hidden' }}>{' '}</span>
				{keyName !== null && <span className="dsv-jv__key">&quot;{keyName}&quot;</span>}
				{keyName !== null && <span className="dsv-jv__colon">: </span>}
				{rendered}
				<span className="dsv-jv__comma">,</span>
			</div>
		);
	}

	const entries = isArray ? value : Object.entries(value);
	const openBracket = isArray ? '[' : '{';
	const closeBracket = isArray ? ']' : '}';
	const isEmpty = isArray ? value.length === 0 : Object.keys(value).length === 0;

	if (isEmpty) {
		return (
			<div className="dsv-jv__line" style={lineStyle}>
				<span className="dsv-jv__toggle" style={{ visibility: 'hidden' }}>{' '}</span>
				{keyName !== null && <span className="dsv-jv__key">&quot;{keyName}&quot;</span>}
				{keyName !== null && <span className="dsv-jv__colon">: </span>}
				<span className="dsv-jv__bracket">{openBracket}{closeBracket}</span>
				<span className="dsv-jv__comma">,</span>
			</div>
		);
	}

	return (
		<>
			<div className="dsv-jv__line" style={lineStyle}>
				<span
					className={`dsv-jv__toggle ${expanded ? 'dsv-jv__toggle--open' : ''}`}
					role="button"
					tabIndex={0}
					aria-expanded={expanded}
					onClick={() => setExpanded(prev => !prev)}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							setExpanded(prev => !prev);
						}
					}}
				>
					{expanded ? '−' : '+'}
				</span>
				{keyName !== null && <span className="dsv-jv__key">&quot;{keyName}&quot;</span>}
				{keyName !== null && <span className="dsv-jv__colon">: </span>}
				<span className="dsv-jv__bracket">{openBracket}</span>
				{!expanded && (
					<>
						<span className="dsv-jv__ellipsis"> ... </span>
						<span className="dsv-jv__bracket">{closeBracket}</span>
						<span className="dsv-jv__comma">,</span>
					</>
				)}
			</div>
			{expanded && (
				<>
					{isArray
						? entries.map((item, i) => (
							<JsonNode key={i} keyName={null} value={item} depth={depth + 1} defaultExpanded={defaultExpanded} />
						))
						: entries.map(([k, v]) => (
							<JsonNode key={k} keyName={k} value={v} depth={depth + 1} defaultExpanded={defaultExpanded} />
						))
					}
					<div className="dsv-jv__line" style={{ paddingLeft: indent }}>
						<span className="dsv-jv__bracket">{closeBracket}</span>
						<span className="dsv-jv__comma">,</span>
					</div>
				</>
			)}
		</>
	);
}

function renderValue(value) {
	if (value === null) return <span className="dsv-jv__null">null</span>;
	if (typeof value === 'boolean') return <span className="dsv-jv__bool">{String(value)}</span>;
	if (typeof value === 'number') return <span className="dsv-jv__number">{value}</span>;
	return <span className="dsv-jv__string">&quot;{value}&quot;</span>;
}

JsonNode.propTypes = {
	keyName: PropTypes.string,
	value: PropTypes.any,
	depth: PropTypes.number,
	defaultExpanded: PropTypes.bool,
};

function JsonViewer({ data }) {
	const [expandAll, setExpandAll] = useState(true);
	const [key, setKey] = useState(0);

	const handleCopy = useCallback(() => {
		navigator.clipboard.writeText(JSON.stringify(data, null, 2));
	}, [data]);

	const handleExpandAll = () => {
		setExpandAll(true);
		setKey(prev => prev + 1);
	};

	const handleCollapseAll = () => {
		setExpandAll(false);
		setKey(prev => prev + 1);
	};

	return (
		<div className="dsv-jv">
			<div className="dsv-jv__toolbar">
				<button className="dsv-jv__toolbar-btn" onClick={handleCopy}>Copy</button>
				<button className="dsv-jv__toolbar-btn" onClick={handleExpandAll}>Expand all</button>
				<button className="dsv-jv__toolbar-btn" onClick={handleCollapseAll}>Collapse all</button>
			</div>
			<div className="dsv-jv__body" key={key}>
				<JsonNode keyName={null} value={data} depth={0} defaultExpanded={expandAll} />
			</div>
		</div>
	);
}

JsonViewer.propTypes = {
	data: PropTypes.any,
};

export default JsonViewer;
